/**
 * Which customer fields the customer drawer's Summary tab shows, and in what order.
 *
 * WHY this lives in shared/: two places must agree on it exactly. The drawer renders it, and the
 * Data Fields page (CIN-6756) marks those same fields "Shows in the customer drawer" so admins
 * know that reordering the Customer data model changes what their team sees first. Before this,
 * the rule lived inline in details.component.js and the page had no way to know it.
 *
 * Kept free of imports so scripts/data-fields-logic.test.js can load it under plain Node.
 */

// Never given a row, so they never take a slot: the name and contact details show in the drawer
// header and contact block instead, and id, company_id and entity_data aren't shown at all.
export const DRAWER_EXCLUDED_CUSTOMER_FIELDS = ['id', 'company_id', 'first_name', 'last_name', 'phone', 'email', 'address1', 'address2', 'city', 'state', 'zip_code', 'country', 'entity_data'];

// Some companies have saved fields with no id, e.g. {"id": null, "name": "test1"} (old legacy-page
// data, seen on Oxi Fresh). Nothing can show, edit or tag a field without an id, and reading one
// crashed the drawer and the Data Fields page, so every list of fields is screened with this.
export const hasFieldId = field => typeof field?.id === 'string' && field.id !== '';

// How many customer fields show before "… N more items".
export const DRAWER_SUMMARY_SLOTS = 5;

// customer_refs__ fields render elsewhere in the drawer. They still occupy a slot when they fall in
// the first five, because the drawer counts positions before skipping them. That matches what
// users see today, so it's preserved rather than "fixed" here.
export const isShownInRows = field => !field.id.startsWith('customer_refs__');

/**
 * @param {Array<{id: string}>} customerFields the Customer data model's fields, in saved order
 * @returns {{ eligible: Array, collapsed: Array, expanded: Array, hiddenCount: number }}
 *   eligible: every field that can occupy a row position
 *   collapsed: the rows shown before "more" is clicked
 *   expanded: the rows shown after it is clicked
 *   hiddenCount: what "… N more items" should say (the old label subtracted 8 instead of 5)
 */
export function getDrawerSummaryFields(customerFields = []) {
  const eligible = customerFields.filter(field => hasFieldId(field) && !DRAWER_EXCLUDED_CUSTOMER_FIELDS.includes(field.id));
  const collapsed = eligible.filter((field, index) => isShownInRows(field) && index < DRAWER_SUMMARY_SLOTS);
  const expanded = eligible.filter(isShownInRows);
  return {
    eligible,
    collapsed,
    expanded,
    hiddenCount: expanded.length - collapsed.length
  };
}